"use strict";
// The single definition of every enumerated value in the system. The API's Drizzle
// schema and the React forms both import from here, so validation and UI can never
// disagree (TECH-STACK Part 4). Values match the Postgres enums in
// `apps/api/src/db/schema/enums.ts` exactly — changing one without the other is a bug.
Object.defineProperty(exports, "__esModule", { value: true });
exports.INTERVIEW_MODES = exports.PANELIST_ROLES = exports.DECISION_RULES = exports.TEMPLATE_STATUSES = exports.QUESTION_TYPES = exports.KNOCKOUT_ACTIONS = exports.CRITERION_KINDS = exports.CRITERION_IMPORTANCES = exports.CRITERION_MARKS = exports.RANKED_BY = exports.RANK_TABS = exports.CRITERION_TYPES = exports.CRITERION_CLASSES = exports.APPROVAL_STEP_STATUSES = exports.INVITE_STATUSES = exports.USER_STATUSES = exports.ACTOR_TYPES = exports.PERMISSION_LEVELS = exports.ROLE_OWNERSHIP_LABELS = exports.ROLE_OWNERSHIPS = exports.ROLE_SCOPE_KIND_LABELS = exports.ROLE_SCOPE_KINDS = exports.ROLE_CODES = exports.IS_TWO_FACTOR_ENFORCED = exports.TWO_FACTOR_MANDATORY_ROLES = exports.ROLES_IMPLIED_BY = exports.ASSIGNABLE_ROLE_KEYS = exports.ROLE_LABELS = exports.ROLE_KEYS = exports.ATTENDANCE_STATUSES = exports.EXAM_SESSION_STATUSES = exports.OFFER_STATUSES = exports.INTERVIEW_STATUSES = exports.JOB_OPENING_STATUSES = exports.REQUISITION_PRIORITIES = exports.REQUISITION_STATUSES = exports.BALL_IN_COURT = exports.DOCUMENT_TEXT_STATUSES = exports.CV_DOWNLOAD_PROBLEMS = exports.IMPORT_CV_STATUSES = exports.IMPORT_SKIP_REASONS = exports.IMPORT_ROW_STATUSES = exports.IMPORT_STATUSES = exports.PHONE_SCREEN_STATUSES = exports.CONTACT_STATUSES = exports.MANAGER_RESPONSES = exports.REVIEW_STATUSES = exports.TERMINAL_APPLICATION_STAGES = exports.CANDIDATE_FACING_STAGE_NAMES = exports.APPLICATION_STAGES = void 0;
exports.ARCHIVE_EXPORT_STATUSES = exports.MESSAGE_STATUSES = exports.MESSAGE_DIRECTIONS = exports.ACTIVITY_TYPES = exports.POSITION_ACTIVE_RECRUITMENT_STATUSES = exports.POSITION_OCCUPIED_STATUSES = exports.POSITION_ROLLUP_STATUSES = exports.POSITION_STATUS_LABELS = exports.POSITION_STATUSES = exports.LANGUAGE_PROFICIENCIES = exports.EDUCATION_LEVEL_LABELS = exports.EDUCATION_LEVELS = exports.WORK_MODES = exports.EMPLOYMENT_TYPES = exports.RETENTION_STATUSES = exports.MANUAL_SLA_PAUSE_REASONS = exports.SLA_PAUSE_REASONS = exports.SLA_CLOCK_STATUSES = exports.NOTIFICATION_CHANNELS = exports.TASK_STATUSES = exports.ASSESSMENT_ATTEMPT_STATUSES = exports.DUPLICATE_FLAG_STATUSES = exports.VERIFICATION_STATUSES = exports.POSTING_STATUSES = exports.INTERVIEW_FLAG_KINDS = exports.RECOMMENDATIONS = exports.STAGE_DECISION_OUTCOMES = exports.SCORECARD_STATUSES = exports.ASSESSMENT_TRIGGERS = void 0;
exports.withImpliedRoles = withImpliedRoles;
/** Appendix A of `01-system-design-epics.md`. */
exports.APPLICATION_STAGES = [
    'new',
    'auto_rejected',
    'screening',
    'assessment',
    'interview_1',
    'interview_2',
    'interview_3',
    'hold',
    'offer',
    'hired_pending_join',
    'joined',
    'rejected',
    'withdrawn',
    'closed_job',
    'transferred',
];
/**
 * §L21 stage name mapping. Internal names must never leak to candidates.
 * `hold` deliberately shows as "Under review" — never tell a candidate they are
 * the backup.
 */
exports.CANDIDATE_FACING_STAGE_NAMES = {
    new: 'Application received',
    auto_rejected: 'Not selected',
    screening: 'Under review',
    assessment: 'Assessment stage',
    interview_1: 'Interview — first round',
    interview_2: 'Interview — second round',
    interview_3: 'Interview — final round',
    hold: 'Under review',
    offer: 'Offer stage',
    hired_pending_join: 'Offer accepted',
    joined: 'Joined',
    rejected: 'Not selected',
    withdrawn: 'Withdrawn',
    closed_job: 'Position no longer available',
    transferred: 'Moved to another role',
};
/** Stages from which no further movement is possible. Starts the D15 retention clock. */
exports.TERMINAL_APPLICATION_STAGES = [
    'auto_rejected',
    'joined',
    'rejected',
    'withdrawn',
    'closed_job',
    'transferred',
];
/**
 * §L25 Review status, §L31: HR's review step inside `screening`. Rejection is not a value:
 * a rejected application leaves `screening` for `rejected` (R2, ADR 0037).
 */
exports.REVIEW_STATUSES = ['to_review', 'shortlisted', 'maybe'];
/** §L25 Manager response, §L31: the manager's answer on a shortlisted application. */
exports.MANAGER_RESPONSES = ['not_sent', 'awaiting', 'yes', 'no', 'hr_proceeded'];
/** §L25 Contact status, §L32. */
exports.CONTACT_STATUSES = [
    'not_contacted',
    'emailed',
    'no_answer',
    'reached',
    'confirmed',
    'not_interested',
    'no_response',
    'not_coming',
];
/**
 * §L25 Phone screening (ADR 0079, workflow review D2): the optional step between Shortlist and
 * Contact. Null means the person never entered it. Pass and Out per round are not values here:
 * a pass raises `phone_screen_passed_round`, an Out is a rejection (the application leaves `screening`).
 */
exports.PHONE_SCREEN_STATUSES = ['in_screening', 'sent_to_contact'];
/** §L25 Import (§L29, R4). */
exports.IMPORT_STATUSES = ['uploaded', 'mapped', 'importing', 'done', 'done_with_problems', 'cancelled', 'discarded'];
/** §L25 Import, "Rows": one CSV row's outcome. */
exports.IMPORT_ROW_STATUSES = ['pending', 'created', 'linked', 'skipped', 'failed'];
exports.IMPORT_SKIP_REASONS = ['already_imported', 'already_applied'];
/** §L25 Import, "A CV". */
exports.IMPORT_CV_STATUSES = ['none', 'queued', 'attached', 'problem'];
/** §L29 "Problems, exactly these", each with its §L26 message. `folder_link` (workflow review D4): a Google Drive folder, not a file. */
exports.CV_DOWNLOAD_PROBLEMS = ['link_private', 'not_found', 'not_a_cv_file', 'too_large', 'timed_out', 'bad_link', 'folder_link'];
/** R4 (filled in R5, §L30 "CV text"): whether a document's plain text has been read. */
exports.DOCUMENT_TEXT_STATUSES = ['pending', 'ready', 'unreadable', 'none'];
exports.BALL_IN_COURT = [
    'recruiter',
    'hiring_manager',
    'candidate',
    'approver',
    'panelist',
    'none',
];
exports.REQUISITION_STATUSES = [
    'draft',
    'submitted',
    'in_approval',
    'approved',
    'rejected',
    'converted',
    'on_hold',
    'filled',
    'partially_filled',
    'cancelled',
    'reopened',
];
/** Three levels (product owner, 2026-09-28). Shown as a dot, never a chip. */
exports.REQUISITION_PRIORITIES = ['low', 'normal', 'urgent'];
exports.JOB_OPENING_STATUSES = [
    'draft',
    'open',
    'on_hold',
    'filled',
    'closed_partial',
    'cancelled',
    'reopened',
];
exports.INTERVIEW_STATUSES = [
    'unscheduled',
    'slots_offered',
    'scheduled',
    'confirmed',
    'rescheduled',
    'completed',
    'candidate_no_show',
    'panelist_no_show',
    'cancelled',
];
exports.OFFER_STATUSES = [
    'draft',
    'in_approval',
    'approved',
    /** §L25: an approver rejects with a mandatory reason, and it returns to draft. */
    'rejected',
    'sent',
    'viewed',
    'negotiating',
    'accepted',
    'declined',
    'expired',
    'joined',
    'accepted_no_show',
    'withdrawn_by_company',
];
exports.EXAM_SESSION_STATUSES = [
    'draft',
    'scheduled',
    'roster_locked',
    'in_progress',
    'results_entry',
    'completed',
    'cancelled',
    'rescheduled',
];
exports.ATTENDANCE_STATUSES = [
    'registered',
    'present',
    'completed',
    'absent',
    'rescheduled',
];
/**
 * §L23 roles: HRL · REC · HM · PAN · APR · INV · CAN, plus ADM and LDR (ADR 0073,
 * 2026-10-29) and HRA (ADR 0085). `admin` alone holds the Admin area; `hr_lead` is shown
 * as "HR" and does all hiring work without it; `leadership` (CEO, COO…) gives the final
 * yes on hire requests and reads everything. `approver` is kept for old data but no
 * longer given. ADR 0085: `hiring_manager` is shown as "Team lead" and `panelist` as
 * "Interviewer" (the keys are stored in data, so only the labels change), and
 * `hr_assistant` helps HR contact candidates and run the day.
 */
exports.ROLE_KEYS = [
    'admin',
    'hr_lead',
    'leadership',
    'recruiter',
    'hiring_manager',
    'panelist',
    'approver',
    'invigilator',
    'hr_assistant',
    'candidate',
];
exports.ROLE_LABELS = {
    admin: 'Admin',
    hr_lead: 'HR',
    leadership: 'Leadership',
    recruiter: 'Recruiter',
    hiring_manager: 'Team lead',
    panelist: 'Interviewer',
    approver: 'Approver',
    invigilator: 'Invigilator',
    hr_assistant: 'HR assistant',
    candidate: 'Candidate',
};
/**
 * The roles a person can actually be given (S4 preset roles). `candidate` is in
 * §L23 for completeness but holds no internal permission anywhere in the matrix, and
 * D16 removed the candidate portal from V1 — so it is never offered in an assignment
 * or in the role builder.
 */
exports.ASSIGNABLE_ROLE_KEYS = [
    'admin',
    'hr_lead',
    'leadership',
    'recruiter',
    'hiring_manager',
    'panelist',
    'invigilator',
    'hr_assistant',
];
/**
 * ADR 0073: an Admin can do everything HR can. Every check written for `hr_lead`
 * therefore also passes for an Admin, because the permission resolver adds `hr_lead`
 * to an Admin's roles. Only the Admin area asks for `admin` itself.
 */
exports.ROLES_IMPLIED_BY = {
    admin: ['hr_lead'],
};
/** The roles held plus the ones they imply, without repeats. */
function withImpliedRoles(roles) {
    const out = new Set(roles);
    for (const role of roles)
        for (const implied of exports.ROLES_IMPLIED_BY[role] ?? [])
            out.add(implied);
    return [...out];
}
/** 2FA is mandatory for these roles (S3 build notes, F1.06). */
exports.TWO_FACTOR_MANDATORY_ROLES = ['admin', 'hr_lead', 'leadership', 'approver'];
/**
 * Off at the product owner's request (2026-09-28) until the product is complete,
 * then back on for admin — see Docs/PROGRESS.md. While off, nobody is made to enrol
 * and nobody is asked for a code at sign-in. Enrolments already made are kept, so
 * switching this back on restores them untouched.
 */
exports.IS_TWO_FACTOR_ENFORCED = false;
/** The short codes used throughout §L23. */
exports.ROLE_CODES = {
    admin: 'ADM',
    hr_lead: 'HRL',
    leadership: 'LDR',
    recruiter: 'REC',
    hiring_manager: 'HM',
    panelist: 'PAN',
    approver: 'APR',
    invigilator: 'INV',
    hr_assistant: 'HRA',
    candidate: 'CAN',
};
/**
 * What a role assignment is scoped to (S4 layer 2). Never optional: "Recruiter" is
 * not a permission, "Recruiter, Venture B and C" is.
 */
exports.ROLE_SCOPE_KINDS = ['organisation', 'venture', 'department'];
exports.ROLE_SCOPE_KIND_LABELS = {
    organisation: 'Whole organisation',
    venture: 'Selected ventures',
    department: 'Selected departments',
};
/** The ownership half of a scope: every record inside it, or only one's own. */
exports.ROLE_OWNERSHIPS = ['all', 'own'];
exports.ROLE_OWNERSHIP_LABELS = {
    all: 'All records in scope',
    own: 'Only their own records',
};
exports.PERMISSION_LEVELS = ['full', 'scoped', 'read', 'none'];
exports.ACTOR_TYPES = ['user', 'system'];
exports.USER_STATUSES = ['invited', 'active', 'suspended', 'deactivated'];
exports.INVITE_STATUSES = ['pending', 'accepted', 'expired', 'revoked'];
exports.APPROVAL_STEP_STATUSES = [
    'pending',
    'approved',
    'rejected',
    'sent_back',
    'skipped',
];
/** L1 criterion model. */
exports.CRITERION_CLASSES = ['must_have', 'nice_to_have'];
exports.CRITERION_TYPES = [
    'skill',
    'experience_years',
    'education_level',
    'certification',
    'language',
    'location',
];
/** §L30, static for now (O24): the importance a recruiter picks; the weight is worked out. */
/** §L30 (R5): the four tabs, settled as O28. Everyone is in exactly one. */
exports.RANK_TABS = ['top_fit', 'maybe', 'low_fit', 'needs_a_look'];
/** ADR 0062: which engine writes a job's main ranking (`match_score`, `stars`, `rank_tab`, `rank_key`): the older screening criteria, or the ideal candidate profile. */
exports.RANKED_BY = ['criteria', 'profile'];
/** §L30 "Not met vs Missing": ✓ ◐ ✗ ?. */
exports.CRITERION_MARKS = ['met', 'partly', 'not_met', 'missing'];
exports.CRITERION_IMPORTANCES = ['must_have', 'important', 'nice_to_have'];
/** §L30 criterion kinds: how the ranking judges a criterion. */
exports.CRITERION_KINDS = ['yes_no', 'level', 'years', 'keywords', 'mentioned'];
/** L8 knockout actions. */
exports.KNOCKOUT_ACTIONS = ['none', 'auto_reject', 'auto_flag', 'score_penalty'];
exports.QUESTION_TYPES = [
    'single_select',
    'multi_select',
    'numeric',
    'boolean',
    'date',
    'text',
    'long_text',
    'file',
];
/** F19.05: a department-submitted role template waits for HR Lead (E18). */
exports.TEMPLATE_STATUSES = ['draft', 'approved', 'rejected'];
/** L4 decision rules. */
exports.DECISION_RULES = ['unanimous', 'majority', 'lead_decides', 'any_veto'];
/** L3: observers are excluded from every calculation. */
exports.PANELIST_ROLES = ['counting', 'observer', 'lead'];
/** Master plan S12: how a round is held (JOB-07). */
exports.INTERVIEW_MODES = ['online', 'in_person', 'phone'];
/** E3 step 5: a test goes out automatically after a screening pass, or by hand. */
exports.ASSESSMENT_TRIGGERS = ['after_screening', 'manual'];
exports.SCORECARD_STATUSES = ['draft', 'submitted', 'waived'];
exports.STAGE_DECISION_OUTCOMES = ['advance', 'reject', 'hold'];
exports.RECOMMENDATIONS = ['strong_yes', 'yes', 'no', 'strong_no'];
/** F11.10: the structured quick-log flags on an interview (S20). */
exports.INTERVIEW_FLAG_KINDS = ['candidate_late', 'cut_short', 'technical_failure'];
exports.POSTING_STATUSES = ['draft', 'published', 'unpublished', 'expired'];
exports.VERIFICATION_STATUSES = [
    'pending',
    'verified',
    'rejected',
    'not_required',
];
exports.DUPLICATE_FLAG_STATUSES = [
    'open',
    'confirmed_duplicate',
    'not_duplicate',
    'merged',
    'unmerged',
];
exports.ASSESSMENT_ATTEMPT_STATUSES = [
    'not_started',
    'in_progress',
    'submitted',
    'auto_graded',
    'manually_graded',
    'expired',
    'abandoned',
    'cancelled',
];
exports.TASK_STATUSES = ['open', 'in_progress', 'done', 'cancelled'];
exports.NOTIFICATION_CHANNELS = ['in_app', 'email'];
exports.SLA_CLOCK_STATUSES = ['running', 'paused', 'stopped', 'breached'];
/** L7 pause conditions. The first six are automatic; the last two are manual. */
exports.SLA_PAUSE_REASONS = [
    'requisition_on_hold',
    'job_on_hold',
    'position_frozen',
    'ball_in_court_candidate',
    'non_working_hours',
    'candidate_reschedule_gap',
    'budget_freeze',
    'hiring_manager_unavailable',
];
/** L7: manual pauses require a reason, and HM is capped at 5 working days. */
exports.MANUAL_SLA_PAUSE_REASONS = [
    'budget_freeze',
    'hiring_manager_unavailable',
];
exports.RETENTION_STATUSES = ['active', 'due', 'anonymised'];
exports.EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contract', 'intern'];
exports.WORK_MODES = ['onsite', 'hybrid', 'remote'];
/** Ordered weakest to strongest — L1 education_level matching depends on the order. */
exports.EDUCATION_LEVELS = [
    'ssc',
    'hsc',
    'diploma',
    'bachelors',
    'masters',
    'doctorate',
];
// Lives here, not in jobs.ts, so icp.ts can be imported by jobs.ts (jobConfigSchema.profile) without a cycle.
exports.EDUCATION_LEVEL_LABELS = {
    ssc: 'SSC',
    hsc: 'HSC',
    diploma: 'Diploma',
    bachelors: "Bachelor's",
    masters: "Master's",
    doctorate: 'Doctorate',
};
/** Ordered weakest to strongest — L1 language matching depends on the order. */
exports.LANGUAGE_PROFICIENCIES = [
    'basic',
    'conversational',
    'professional',
    'native',
];
/**
 * A budgeted seat's status, per `00-MASTER-PLAN` S5 and E1.
 *
 * S1 shipped `vacant | occupied | becoming_vacant | frozen | closed`, which no
 * document asks for: `occupied` is E1's `filled`, `closed` is archiving rather than
 * a status, and `planned` and `in_recruitment` were missing entirely — so the org
 * tree could not count two of the five figures it is specified to show. Corrected
 * here and in migration 0004 (ADR 0007).
 *
 * Ordered as the org tree reads them, not alphabetically.
 */
exports.POSITION_STATUSES = [
    'planned',
    'vacant',
    'in_recruitment',
    'filled',
    'becoming_vacant',
    'frozen',
];
exports.POSITION_STATUS_LABELS = {
    planned: 'Planned',
    vacant: 'Vacant',
    in_recruitment: 'In recruitment',
    filled: 'Filled',
    becoming_vacant: 'Becoming vacant',
    frozen: 'Frozen',
};
/**
 * The statuses the org tree rolls up, in display order. `becoming_vacant` is
 * deliberately absent: the seat is still filled until the leaver's last working
 * day, and counting it twice would make the columns stop summing to the total.
 */
exports.POSITION_ROLLUP_STATUSES = [
    'filled',
    'vacant',
    'in_recruitment',
    'planned',
    'frozen',
];
/**
 * A seat that counts as occupied today. `becoming_vacant` is here because the
 * person has not left yet — E1: the seat shows as becoming vacant *on* the last
 * working day, which is when it turns `vacant`.
 */
exports.POSITION_OCCUPIED_STATUSES = ['filled', 'becoming_vacant'];
/**
 * Seats that count towards active recruitment.
 *
 * S5 acceptance: "frozen positions are excluded from active recruitment counts."
 * A frozen seat keeps whatever it was doing before the freeze — it is simply not
 * being worked on, which is exactly why the freeze pauses its SLA clock (L7).
 */
exports.POSITION_ACTIVE_RECRUITMENT_STATUSES = ['in_recruitment'];
exports.ACTIVITY_TYPES = [
    'note',
    'call',
    'email_out',
    'email_in',
    'sms',
    'meeting',
    'system',
];
exports.MESSAGE_DIRECTIONS = ['outbound', 'inbound'];
/** S15: `scheduled` waits for its moment; `cancelled` was undone before it went. */
exports.MESSAGE_STATUSES = ['queued', 'sent', 'delivered', 'failed', 'bounced', 'scheduled', 'cancelled'];
exports.ARCHIVE_EXPORT_STATUSES = ['pending', 'running', 'completed', 'failed'];
//# sourceMappingURL=enums.js.map